// plan-05 S02: hash → maturity computation — verbatim port from v1

import type { HexGrid, Hex } from './geometry'
import { getNeighbors, getRing1, getRing2, getOutermost } from './geometry'
import { strHash, makePRNG } from './prng'

export interface MaturityResult {
  hits: number[]
  eligible: Set<number>
  goldenGlow: Set<number>
  bordersBlank: Set<number>
  hashStr: string
}

function isPrime(n: number): boolean {
  if (n < 2) return false
  if (n === 2) return true
  if (n % 2 === 0) return false
  for (let i = 3; i * i <= n; i += 2) {
    if (n % i === 0) return false
  }
  return true
}

function repeatRunLength(hashStr: string): number {
  let max = 1, cur = 1
  for (let i = 1; i < hashStr.length; i++) {
    cur = hashStr[i] === hashStr[i - 1] ? cur + 1 : 1
    if (cur > max) max = cur
  }
  return max >= 3 ? 3 : max >= 2 ? 2 : 0
}

function prime3Substrings(hashStr: string): boolean {
  const zeroCount = hashStr.split('').filter(c => c === '0').length
  if (zeroCount <= 2) return false
  for (let i = 0; i <= hashStr.length - 4; i++) {
    if (hashStr[i] === '0') continue
    const n = parseInt(hashStr.slice(i, i + 4))
    if (isPrime(n)) return true
  }
  return false
}

function neighborMaturitySum(grid: HexGrid, h: Hex, hits: number[], eligible: Set<number>): number {
  return getNeighbors(grid, h).reduce((sum, nb) => {
    return sum + (eligible.has(nb.seqNum) ? hits[nb.seqNum] : 0)
  }, 0)
}

export function computeMaturity(grid: HexGrid, word: string): MaturityResult {
  const { ordered, centerHex, total } = grid
  const seed = strHash(word)
  const hashStr = seed.toString()
  const rng = makePRNG(seed)

  // 500 RNG draws
  const hits = new Array(total).fill(0)
  for (let i = 0; i < 500; i++) hits[Math.floor(rng() * total)]++

  // center hex always max
  const maxHits = Math.max(...hits)
  hits[centerHex.seqNum] = maxHits

  // repeat-run-length boosts
  const runLen = repeatRunLength(hashStr)
  if (runLen >= 3) {
    hits[centerHex.seqNum] *= 4
    getRing1(grid).forEach(h => { hits[h.seqNum] *= 3 })
    getRing2(grid).forEach(h => { hits[h.seqNum] *= 2 })
  } else if (runLen === 2) {
    getRing1(grid).forEach(h => { hits[h.seqNum] *= 2 })
  }

  // pruning: any hex with at least one zero-hit neighbor → set to 0
  const toPrune = new Set<number>()
  ordered.forEach((h, i) => {
    if (hits[i] === 0) return
    if (getNeighbors(grid, h).some(nb => hits[nb.seqNum] === 0)) toPrune.add(i)
  })
  toPrune.forEach(i => { hits[i] = 0 })

  // eligible: hits > 2
  const eligible = new Set<number>()
  ordered.forEach((h, i) => {
    if (hits[i] > 2) eligible.add(h.seqNum)
  })

  // golden glow: hits > 4 AND isPrime(seqNum)
  const goldenGlow = new Set<number>()
  const bordersBlank = new Set<number>()
  ordered.forEach((h, i) => {
    if (hits[i] === 0) return
    if (hits[i] > 4 && isPrime(h.seqNum)) goldenGlow.add(h.seqNum)
    if (getNeighbors(grid, h).some(nb => hits[nb.seqNum] === 0)) bordersBlank.add(h.seqNum)
  })

  return { hits, eligible, goldenGlow, bordersBlank, hashStr }
}

// helpers exported for lines.ts and decode.ts
export { isPrime, neighborMaturitySum, prime3Substrings, repeatRunLength, getOutermost }
